import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import { observer } from 'mobx-react';

import type { PageConfig } from 'src/libs/PageConfig';

import type { IProspectEmailStore } from '../../../stores/JobApplicationStore/ProspectEmailStore';
import { ACTION_ERROR } from '../../../stores/state';

import { parseEmailSubjectLength } from './../../../utils/parseEmailSubjectLength';
import translations from './.vocab';
import SendProspectEmailForm from './SendProspectEmailForm/SendProspectEmailForm';

interface Props {
  prospectEmailStore: IProspectEmailStore;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const SendProspectEmail = ({ prospectEmailStore, addCustomError }: Props) => {
  const { t } = useTranslations(translations);
  const showToast = useToast();

  const onSubmitSuccess = () => {
    showToast({
      tone: 'positive',
      message: t('Email sent'),
      closeLabel: t('Close'),
    });
  };

  const onSendProspectEmail = (
    subject: string,
    message: string,
    enableReplies: boolean,
  ) =>
    prospectEmailStore
      .sendEmail({
        subject,
        message,
        enableReplies,
        addCustomError,
      })
      .then((state: string) => state !== ACTION_ERROR);

  const emailSubject = t(
    'advertiserName has responded to your application for jobTitle',
    {
      advertiserName:
        prospectEmailStore.rootStore.userStore.user.advertiserName,
      jobTitle: prospectEmailStore.rootStore.job.jobInformation.title,
    },
  );

  const messageDefaultText = t('Hi firstName,', {
    firstName: prospectEmailStore.jobApplication.firstName,
  });

  return (
    <SendProspectEmailForm
      onSubmitSuccess={onSubmitSuccess}
      onSendProspectEmail={onSendProspectEmail}
      fromName={prospectEmailStore.rootStore.userStore.user.name}
      fromEmail={prospectEmailStore.rootStore.userStore.user.email}
      messageDefaultText={messageDefaultText}
      subjectDefaultText={parseEmailSubjectLength(emailSubject)}
      selectedJobApplicationsCount={0}
      buttonSize="small"
      addCustomError={addCustomError}
    />
  );
};

export default observer(SendProspectEmail);
